import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileSearch,
  FileText,
  Package,
  Receipt,
  Search as SearchIcon,
  StickyNote,
  Target,
  Users,
  Wrench,
  X,
} from 'lucide-react-native';
import { ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { searchEverything, type SearchHit, type SearchKind } from '@/lib/search';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

const KIND_META: Record<
  SearchKind,
  { Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>; tint: string; bg: string; label: string }
> = {
  system:    { Icon: Wrench,        tint: Palette.forest,    bg: '#EAF0EA',                label: 'System' },
  task:      { Icon: CheckCircle2,  tint: Palette.forest,    bg: '#DCE7E1',                label: 'Task' },
  inventory: { Icon: Package,       tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.14)',  label: 'Inventory' },
  expense:   { Icon: Receipt,       tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.18)',  label: 'Expense' },
  contact:   { Icon: Users,         tint: Palette.forest,    bg: '#EAF0EA',                label: 'Contact' },
  document:  { Icon: FileText,      tint: Palette.clay,      bg: 'rgba(176,95,60,0.13)',   label: 'Document' },
  quote:     { Icon: FileSearch,    tint: Palette.forest,    bg: '#DCE7E1',                label: 'Quote' },
  goal:      { Icon: Target,        tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.14)',  label: 'Goal' },
  note:      { Icon: StickyNote,    tint: Palette.clay,      bg: 'rgba(176,95,60,0.10)',   label: 'Note' },
};

const GROUP_ORDER: SearchKind[] = ['task', 'system', 'inventory', 'expense', 'quote', 'goal', 'contact', 'document', 'note'];

export default function SearchScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    (async () => {
      const p = await getActiveProperty(db);
      setPropertyId(p?.id ?? null);
    })();
  }, [db]);

  useEffect(() => {
    if (!propertyId) return;
    if (debounce.current) clearTimeout(debounce.current);
    if (term.trim().length < 2) {
      setHits([]);
      return;
    }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      const out = await searchEverything(db, propertyId, term);
      setHits(out);
      setSearching(false);
    }, 180);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [term, propertyId, db]);

  const grouped = useMemo(() => {
    const map: Record<SearchKind, SearchHit[]> = {
      system: [], task: [], inventory: [], expense: [], contact: [], document: [],
      quote: [], goal: [], note: [],
    };
    for (const h of hits) map[h.kind].push(h);
    return GROUP_ORDER
      .map((k) => ({ kind: k, items: map[k] }))
      .filter((g) => g.items.length > 0);
  }, [hits]);

  const goTo = (hit: SearchHit) => {
    switch (hit.kind) {
      case 'system':    router.push(`/system/${hit.id}` as never); break;
      case 'task':      router.push(`/task/${hit.id}` as never); break;
      case 'inventory': router.push(`/inventory/${hit.id}` as never); break;
      case 'expense':   router.push(`/expenses/${hit.id}` as never); break;
      case 'contact':   router.push(`/contact/${hit.id}` as never); break;
      case 'document':  router.push('/media' as never); break;
      case 'quote':     router.push(`/quotes/${hit.id}` as never); break;
      case 'goal':      router.push(`/projects/${hit.id}` as never); break;
      case 'note':      router.push('/notes' as never); break;
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Search</Text>
        <View style={{ width: 36 }} />
      </View>

      <View style={styles.searchWrap}>
        <View style={styles.searchBox}>
          <SearchIcon size={18} color={Palette.inkFaint} strokeWidth={1.8} />
          <TextInput
            value={term}
            onChangeText={setTerm}
            placeholder="Search systems, tasks, expenses, contacts…"
            placeholderTextColor={Palette.inkFaint}
            autoFocus
            returnKeyType="search"
            style={[styles.input, webNoOutline]}
          />
          {term ? (
            <Pressable onPress={() => setTerm('')} hitSlop={10}>
              <X size={16} color={Palette.inkFaint} strokeWidth={2} />
            </Pressable>
          ) : null}
        </View>
        <Text style={styles.hint}>
          {term.trim().length < 2
            ? 'Type at least 2 characters…'
            : searching
            ? 'Searching…'
            : `${hits.length} result${hits.length === 1 ? '' : 's'}`}
        </Text>
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 80 }} keyboardShouldPersistTaps="handled">
        {term.trim().length < 2 ? (
          <EmptyState />
        ) : grouped.length === 0 && !searching ? (
          <View style={styles.noHits}>
            <Text style={styles.noHitsTitle}>No matches</Text>
            <Text style={styles.noHitsBody}>
              Try a different spelling, or part of a brand, model, or vendor name.
            </Text>
          </View>
        ) : (
          grouped.map((g) => {
            const meta = KIND_META[g.kind];
            return (
              <View key={g.kind}>
                <Text style={styles.sectionLabel}>
                  {meta.label.toUpperCase()} · {g.items.length}
                </Text>
                <View style={styles.card}>
                  {g.items.map((h, i) => (
                    <HitRow
                      key={`${h.kind}-${h.id}`}
                      hit={h}
                      onPress={() => goTo(h)}
                      last={i === g.items.length - 1}
                    />
                  ))}
                </View>
              </View>
            );
          })
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

function EmptyState() {
  return (
    <View style={styles.empty}>
      <Text style={styles.emptyTitle}>One search box, every drawer.</Text>
      <Text style={styles.emptyBody}>
        Find a serial number, a vendor, a half-remembered task — Homestead
        looks everywhere at once.
      </Text>
    </View>
  );
}

function HitRow({
  hit,
  onPress,
  last,
}: {
  hit: SearchHit;
  onPress: () => void;
  last?: boolean;
}) {
  const meta = KIND_META[hit.kind];
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.row, last && { borderBottomWidth: 0 }, pressed && { opacity: 0.85 }]}>
      <View style={[styles.rowIcon, { backgroundColor: meta.bg }]}>
        <meta.Icon size={18} color={meta.tint} strokeWidth={1.8} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.rowTitle} numberOfLines={2}>{hit.title || meta.label}</Text>
        {hit.subtitle || hit.meta ? (
          <Text style={styles.rowMeta} numberOfLines={1}>
            {[hit.subtitle, hit.meta].filter(Boolean).join(' · ')}
          </Text>
        ) : null}
      </View>
      <ChevronRight size={16} color={Palette.inkFaint} strokeWidth={1.7} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  searchWrap: { paddingHorizontal: Spacing.four, paddingTop: Spacing.two },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    paddingHorizontal: 14, paddingVertical: 12, borderRadius: 14,
  },
  input: {
    flex: 1, fontFamily: Fonts.sansSemibold, fontSize: 14.5, color: Palette.ink,
    padding: 0,
  },
  hint: {
    marginTop: 8, marginLeft: 4,
    fontSize: 12.5, fontFamily: Fonts.sansBold, color: Palette.inkFaint,
    letterSpacing: 0.3,
  },
  sectionLabel: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkFaint,
    letterSpacing: 0.7, marginBottom: 8,
  },
  card: {
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, paddingHorizontal: 14,
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: Palette.line,
  },
  rowIcon: {
    width: 36, height: 36, borderRadius: 11,
    alignItems: 'center', justifyContent: 'center',
  },
  rowTitle: { fontSize: 13.5, fontFamily: Fonts.sansBold, color: Palette.ink },
  rowMeta: {
    marginTop: 2, fontSize: 12.5, fontFamily: Fonts.sansMedium,
    color: Palette.inkSoft,
  },
  empty: {
    marginTop: Spacing.five, paddingHorizontal: 32,
    alignItems: 'center',
  },
  emptyTitle: {
    fontFamily: Fonts.serifBold, fontSize: 19, color: Palette.ink, textAlign: 'center',
    letterSpacing: -0.3,
  },
  emptyBody: {
    marginTop: 8, fontSize: 13, lineHeight: 20,
    color: Palette.inkSoft, fontFamily: Fonts.sansMedium, textAlign: 'center',
  },
  noHits: {
    marginTop: Spacing.four, paddingHorizontal: 32,
    alignItems: 'center',
  },
  noHitsTitle: {
    fontFamily: Fonts.serifBold, fontSize: 17, color: Palette.ink,
  },
  noHitsBody: {
    marginTop: 6, fontSize: 13, lineHeight: 19,
    color: Palette.inkSoft, fontFamily: Fonts.sansMedium, textAlign: 'center',
  },
});
